import { Home, Search, WarningAlt } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, Navigate, useRouteError } from 'react-router';
import { Button, ButtonLink, EmptyState, StateLayout } from '@/ui';
import { useHelp } from '../shell/HelpContext';
import { useSession } from '../AppServicesContext';
import { buildNavigation, sectionHref, sections as registeredSections, type SectionDefinition } from '../registry';
import s from '../layouts/Layout.module.css';
import { useDocumentTitle } from './SectionPage';

/** Главная области: первый доступный раздел по порядку навигации. */
export function HomeRedirect({ area, sections = registeredSections }: { area: 'clinic' | 'backoffice'; sections?: ReadonlyArray<SectionDefinition> }) {
  const { t } = useTranslation('shell');
  const help = useHelp();
  const { state, roles } = useSession();
  const navRoles = area === 'backoffice' ? (state.user?.globalRoles ?? []) : roles;
  const first = buildNavigation(sections, area, navRoles)[0]?.items[0];
  useDocumentTitle(first ? null : t('home.emptyTitle'));
  if (first) return <Navigate to={sectionHref(first)} replace />;
  return (
    <div className={s.fullPageStatus}>
      <EmptyState
        icon={Home}
        title={t('home.emptyTitle')}
        description={t('home.emptyDescription')}
        actions={
          <Button variant="tertiary" onClick={() => help.open()}>
            {t('home.contactSupport')}
          </Button>
        }
      />
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useTranslation('shell');
  useDocumentTitle(t('notFound.title'));
  return (
    <div className={s.fullPageStatus}>
      <StateLayout
        icon={Search}
        title={t('notFound.title')}
        description={t('notFound.description')}
        actions={
          <ButtonLink to="/" variant="primary">
            {t('page.home')}
          </ButtonLink>
        }
      />
    </div>
  );
}

/** Ошибка маршрутизации или загрузки чанка после обновления версии. */
export function RouteErrorPage() {
  const { t } = useTranslation('shell');
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  useDocumentTitle(notFound ? t('notFound.title') : t('page.crashTitle'));
  if (notFound) return <NotFoundPage />;
  return (
    <div className={s.fullPageStatus}>
      <StateLayout
        icon={WarningAlt}
        tone="error"
        role="alert"
        title={t('page.crashTitle')}
        description={t('page.crashDescription')}
        actions={
          <>
            <Button onClick={() => window.location.reload()}>{t('page.reload')}</Button>
            <ButtonLink to="/" variant="tertiary">
              {t('page.home')}
            </ButtonLink>
          </>
        }
      />
    </div>
  );
}
